/*
 Visualizzatore PDF della piattaforma.

 Prima qui c'era un `<embed type="application/pdf">`, cioe' il visualizzatore incorporato del
 browser. Funzionava, ma non e' governabile: lo zoom riparte dall'angolo della pagina invece
 che dal punto guardato, e in un riquadro stretto accanto a un modulo lo scorrimento diventa
 un lavoro. Chi valida attestati fa una cosa sola, cinquanta volte di fila: ingrandire un
 codice fiscale stampato piccolo su una scansione e ricopiarlo nel campo accanto.

 Quindi il documento si disegna con pdf.js e i comandi sono nostri:
   - lo zoom tiene fermo il punto sotto il puntatore, non l'angolo della pagina;
   - la pagina si sposta trascinandola, come una mappa;
   - il testo resta selezionabile, perche' copiare un codice fiscale e' meglio che ribatterlo.
 */
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import styled from "styled-components";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import {
  ChevronLeft,
  ChevronRight,
  Maximize2,
  Minus,
  Plus,
  RotateCw,
} from "lucide-react";

/*
 Il worker viene servito come file statico da `public/`, dove lo copia
 `scripts/copia-worker-pdf.js` a ogni start e a ogni build. La via ufficiale
 (`new URL(..., import.meta.url)`) non e' percorribile: react-scripts 5 non supporta
 `import.meta`.
 */
pdfjs.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.mjs";

const SCALA_MINIMA = 0.5;
const SCALA_MASSIMA = 6;
const PASSO_BOTTONI = 0.25;

const Contenitore = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 0;
  height: 100%;
  border-radius: 8px;
  overflow: hidden;
  background-color: #3a3f44;
`;

const Barra = styled.div`
  display: flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.35rem 0.6rem;
  background-color: #2b2f33;
  color: #dfe4e8;
  font-size: 0.75rem;
  flex: 0 0 auto;
`;

const ComandoBarra = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  color: inherit;
  cursor: pointer;
  padding: 0.2rem;
  border-radius: 4px;

  &:hover:not(:disabled) {
    background-color: #3f464c;
  }
  &:disabled {
    opacity: 0.35;
    cursor: default;
  }
`;

const Separatore = styled.span`
  width: 1px;
  height: 1rem;
  background-color: #454c52;
  margin: 0 0.3rem;
`;

const Percentuale = styled.span`
  min-width: 3.2rem;
  text-align: center;
  font-variant-numeric: tabular-nums;
`;

const Spazio = styled.span`
  margin-left: auto;
`;

/*
 Il riquadro che scorre. `overscroll-behavior: contain` impedisce che, arrivati in fondo al
 documento, la rotellina continui sulla pagina sottostante.
 */
const AreaDocumento = styled.div<{ $inTrascinamento: boolean }>`
  flex: 1 1 auto;
  min-height: 0;
  overflow: auto;
  overscroll-behavior: contain;
  padding: 0.8rem;
  display: flex;
  align-items: flex-start;
  cursor: ${({ $inTrascinamento }) => ($inTrascinamento ? "grabbing" : "grab")};
  user-select: ${({ $inTrascinamento }) => ($inTrascinamento ? "none" : "auto")};

  .react-pdf__Page {
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.45);
  }
`;

const Messaggio = styled.p`
  color: #cfd6dc;
  text-align: center;
  padding: 2rem 1rem;
  margin: 0;
`;

/*
 Lo spazio della pagina e' riservato qui, con misure esplicite, invece di lasciarlo decidere
 al disegno di pdf.js. Quando la scala cambia, pdf.js rifa' il canvas e per un istante il
 contenuto non ha altezza: se l'area che scorre si sgonfia, il browser riporta lo scorrimento
 a zero e lo zoom "sul punto" salta comunque all'inizio del documento. Tenendo ferma la
 cornice il salto non avviene.
 */
const CornicePagina = styled.div`
  flex: 0 0 auto;
  position: relative;

  /*
   La pagina si centra con i margini automatici e non con \`justify-content: center\` sul
   contenitore. Sono equivalenti finche' la pagina ci sta, ma appena e' piu' larga del
   riquadro — cioe' appena si ingrandisce — il centraggio del contenitore fa traboccare
   l'eccedenza da entrambi i lati mentre \`scrollLeft\` si ferma a zero: la meta' sinistra del
   documento diventa irraggiungibile. Con \`margin: auto\` il margine si annulla da se' quando
   lo spazio finisce, e tutta la pagina resta percorribile.
   */
  margin: 0 auto;
`;

type Props = {
  /** Il PDF da mostrare: URL o data URL. `null` quando non ce n'e' uno. */
  sorgente: string | null;
  /** Testo mostrato al posto del documento quando non c'e' niente da vedere. */
  testoVuoto?: string;
};

const VisorePdf: React.FC<Props> = ({
  sorgente,
  testoVuoto = "Nessun PDF associato a questo attestato",
}) => {
  /*
   Il riquadro sta in uno stato e non solo in un ref: quando non c'e' un documento non viene
   nemmeno disegnato, e un ref non avvisa nessuno quando poi compare. Tenendolo nello stato,
   gli effetti che devono agganciarcisi ripartono nel momento in cui esiste.
   */
  const [nodoArea, setNodoArea] = useState<HTMLDivElement | null>(null);
  const areaRef = useRef<HTMLDivElement | null>(null);
  areaRef.current = nodoArea;

  const [pagine, setPagine] = useState(0);
  const [paginaCorrente, setPaginaCorrente] = useState(1);
  const [rotazione, setRotazione] = useState(0);
  const [errore, setErrore] = useState<string | null>(null);

  /*
   `larghezzaBase` e' la larghezza a cui la pagina riempie il riquadro: la scala 1 significa
   "adattato alla larghezza", non "100% del formato carta". E' la scala che serve a chi
   guarda un attestato dentro una colonna, non dentro una finestra intera.
   */
  const [larghezzaBase, setLarghezzaBase] = useState(0);
  const [scala, setScala] = useState(1);

  /* Rapporto altezza/larghezza della pagina non ruotata, per riservarne lo spazio. */
  const [proporzione, setProporzione] = useState<number | null>(null);

  /*
   Lo stato serve solo al cursore; il gesto si regola sul ref, che e' aggiornato subito e non
   al prossimo disegno — altrimenti i primi spostamenti del mouse leggerebbero il valore
   vecchio e il trascinamento partirebbe a scatti.
   */
  const [inTrascinamento, setInTrascinamento] = useState(false);
  const trascinamentoAttivo = useRef(false);
  const trascinamento = useRef({ x: 0, y: 0, left: 0, top: 0 });

  /* Dove riportare lo scorrimento dopo un cambio di scala, per tenere fermo un punto. */
  const ancoraggio = useRef<{ left: number; top: number } | null>(null);

  /*
   La larghezza si osserva invece di leggerla al ridimensionamento della finestra: la colonna
   del documento cambia dimensione anche quando la finestra resta ferma.
   */
  useLayoutEffect(() => {
    if (!nodoArea) return;
    const misura = () => {
      const stile = window.getComputedStyle(nodoArea);
      const orizzontali =
        parseFloat(stile.paddingLeft) + parseFloat(stile.paddingRight);
      setLarghezzaBase(Math.max(nodoArea.clientWidth - orizzontali, 120));
    };
    misura();
    const osservatore = new ResizeObserver(misura);
    osservatore.observe(nodoArea);
    return () => osservatore.disconnect();
  }, [nodoArea]);

  /* Documento nuovo: si riparte adattati alla larghezza, in cima, alla prima pagina. */
  useEffect(() => {
    setScala(1);
    setRotazione(0);
    setPaginaCorrente(1);
    setProporzione(null);
    setErrore(null);
    const area = areaRef.current;
    if (area) {
      area.scrollTop = 0;
      area.scrollLeft = 0;
    }
  }, [sorgente]);

  useLayoutEffect(() => {
    const area = areaRef.current;
    if (!area || !ancoraggio.current) return;
    area.scrollLeft = ancoraggio.current.left;
    area.scrollTop = ancoraggio.current.top;
    ancoraggio.current = null;
  }, [scala]);

  /*
   Il cuore della faccenda. Ingrandire attorno a un punto vuol dire che quel punto deve
   restare dov'e' sullo schermo: tutto il resto gli si allarga intorno. Senza questo conto
   lo zoom sposta la vista e si perde il punto che si stava leggendo — il difetto per cui
   questo componente esiste.
   */
  const zoomaVerso = useCallback(
    (nuovaScalaGrezza: number, puntoX?: number, puntoY?: number) => {
      const area = areaRef.current;
      if (!area) return;

      setScala((scalaPrecedente) => {
        const nuovaScala = Math.min(
          SCALA_MASSIMA,
          Math.max(SCALA_MINIMA, nuovaScalaGrezza)
        );
        if (nuovaScala === scalaPrecedente) return scalaPrecedente;

        const rettangolo = area.getBoundingClientRect();
        const ancoraX =
          puntoX === undefined
            ? area.clientWidth / 2
            : puntoX - rettangolo.left;
        const ancoraY =
          puntoY === undefined
            ? area.clientHeight / 2
            : puntoY - rettangolo.top;

        const rapporto = nuovaScala / scalaPrecedente;
        ancoraggio.current = {
          left: (area.scrollLeft + ancoraX) * rapporto - ancoraX,
          top: (area.scrollTop + ancoraY) * rapporto - ancoraY,
        };
        return nuovaScala;
      });
    },
    []
  );

  /*
   La rotellina ingrandisce solo con Ctrl premuto, come ovunque: da sola deve scorrere il
   documento, altrimenti si ingrandisce per sbaglio ogni volta che si cerca la riga dopo.
   L'ascoltatore e' aggiunto a mano perche' React registra `onWheel` come passivo, e un
   ascoltatore passivo non puo' chiamare `preventDefault` per impedire lo zoom del browser.
   */
  useEffect(() => {
    const area = nodoArea;
    if (!area) return;

    const allaRotellina = (evento: WheelEvent) => {
      if (!evento.ctrlKey && !evento.metaKey) return;
      evento.preventDefault();
      const fattore = evento.deltaY < 0 ? 1.12 : 1 / 1.12;
      setScala((corrente) => {
        const nuova = Math.min(
          SCALA_MASSIMA,
          Math.max(SCALA_MINIMA, corrente * fattore)
        );
        if (nuova === corrente) return corrente;
        const rettangolo = area.getBoundingClientRect();
        const ancoraX = evento.clientX - rettangolo.left;
        const ancoraY = evento.clientY - rettangolo.top;
        const rapporto = nuova / corrente;
        ancoraggio.current = {
          left: (area.scrollLeft + ancoraX) * rapporto - ancoraX,
          top: (area.scrollTop + ancoraY) * rapporto - ancoraY,
        };
        return nuova;
      });
    };

    area.addEventListener("wheel", allaRotellina, { passive: false });
    return () => area.removeEventListener("wheel", allaRotellina);
  }, [nodoArea]);

  /*
   Trascinamento della pagina, in un solo posto: premere, spostare, rilasciare.

   Non si puo' decidere al `mousedown` se il gesto sia un trascinamento o una selezione di
   testo, perche' pdf.js stende sopra la pagina un livello di testo trasparente che copre
   tutto: il bersaglio dell'evento e' quasi sempre quello, anche sul bianco. Una versione
   precedente rinunciava a trascinare quando il bersaglio era il livello di testo, e il
   risultato era che il trascinamento non funzionava **mai**.

   Quindi si aspetta di vedere il movimento. Sotto i quattro pixel il gesto resta un clic e
   il browser fa il suo (selezione, doppio clic sulla parola); oltre, diventa un
   trascinamento e la selezione in corso viene annullata.
   */
  useEffect(() => {
    const area = nodoArea;
    if (!area) return;

    let candidato = false;

    const premuto = (evento: MouseEvent) => {
      if (evento.button !== 0) return;
      candidato = true;
      trascinamento.current = {
        x: evento.clientX,
        y: evento.clientY,
        left: area.scrollLeft,
        top: area.scrollTop,
      };
    };

    const mosso = (evento: MouseEvent) => {
      if (!candidato) return;
      const scostamentoX = evento.clientX - trascinamento.current.x;
      const scostamentoY = evento.clientY - trascinamento.current.y;

      if (!trascinamentoAttivo.current) {
        if (Math.abs(scostamentoX) < 4 && Math.abs(scostamentoY) < 4) return;
        trascinamentoAttivo.current = true;
        setInTrascinamento(true);
        window.getSelection()?.removeAllRanges();
      }

      /*
       Senza questo il browser continua a estendere la selezione del testo sotto il
       puntatore mentre la pagina scorre, e si finisce con mezzo documento evidenziato.
       */
      evento.preventDefault();
      area.scrollLeft = trascinamento.current.left - scostamentoX;
      area.scrollTop = trascinamento.current.top - scostamentoY;
    };

    const rilasciato = () => {
      candidato = false;
      if (trascinamentoAttivo.current) {
        trascinamentoAttivo.current = false;
        setInTrascinamento(false);
      }
    };

    area.addEventListener("mousedown", premuto);
    window.addEventListener("mousemove", mosso);
    window.addEventListener("mouseup", rilasciato);
    return () => {
      area.removeEventListener("mousedown", premuto);
      window.removeEventListener("mousemove", mosso);
      window.removeEventListener("mouseup", rilasciato);
    };
  }, [nodoArea]);

  const adattaAllaLarghezza = () => {
    const area = areaRef.current;
    ancoraggio.current = { left: 0, top: area ? area.scrollTop : 0 };
    setScala(1);
  };

  if (!sorgente) {
    return (
      <Contenitore>
        <Messaggio>{testoVuoto}</Messaggio>
      </Contenitore>
    );
  }

  return (
    <Contenitore>
      <Barra>
        <ComandoBarra
          type="button"
          title="Riduci"
          aria-label="Riduci"
          onClick={() => zoomaVerso(scala - PASSO_BOTTONI)}
          disabled={scala <= SCALA_MINIMA}
        >
          <Minus size={15} />
        </ComandoBarra>
        <Percentuale>{Math.round(scala * 100)}%</Percentuale>
        <ComandoBarra
          type="button"
          title="Ingrandisci"
          aria-label="Ingrandisci"
          onClick={() => zoomaVerso(scala + PASSO_BOTTONI)}
          disabled={scala >= SCALA_MASSIMA}
        >
          <Plus size={15} />
        </ComandoBarra>

        <Separatore />

        <ComandoBarra
          type="button"
          title="Adatta alla larghezza"
          aria-label="Adatta alla larghezza"
          onClick={adattaAllaLarghezza}
        >
          <Maximize2 size={15} />
        </ComandoBarra>
        <ComandoBarra
          type="button"
          title="Ruota"
          aria-label="Ruota"
          onClick={() => setRotazione((gradi) => (gradi + 90) % 360)}
        >
          <RotateCw size={15} />
        </ComandoBarra>

        {pagine > 1 && (
          <>
            <Separatore />
            <ComandoBarra
              type="button"
              title="Pagina precedente"
              aria-label="Pagina precedente"
              onClick={() => setPaginaCorrente((n) => Math.max(1, n - 1))}
              disabled={paginaCorrente <= 1}
            >
              <ChevronLeft size={15} />
            </ComandoBarra>
            <span>
              {paginaCorrente} / {pagine}
            </span>
            <ComandoBarra
              type="button"
              title="Pagina successiva"
              aria-label="Pagina successiva"
              onClick={() => setPaginaCorrente((n) => Math.min(pagine, n + 1))}
              disabled={paginaCorrente >= pagine}
            >
              <ChevronRight size={15} />
            </ComandoBarra>
          </>
        )}

        <Spazio />
        <span>Ctrl + rotellina per ingrandire</span>
      </Barra>

      <AreaDocumento ref={setNodoArea} $inTrascinamento={inTrascinamento}>
        {errore ? (
          <Messaggio>{errore}</Messaggio>
        ) : (
          <Document
            file={sorgente}
            onLoadSuccess={({ numPages }) => {
              setPagine(numPages);
              setErrore(null);
            }}
            onLoadError={() =>
              setErrore("Non è stato possibile aprire il documento.")
            }
            loading={<Messaggio>Apertura del documento…</Messaggio>}
            error={<Messaggio>Non è stato possibile aprire il documento.</Messaggio>}
          >
            {larghezzaBase > 0 &&
              (() => {
                const larghezza = larghezzaBase * scala;
                const ruotataDiLato = rotazione % 180 !== 0;
                const rapporto =
                  proporzione === null
                    ? null
                    : ruotataDiLato
                      ? 1 / proporzione
                      : proporzione;
                return (
                  <CornicePagina
                    style={{
                      width: larghezza,
                      height: rapporto ? larghezza * rapporto : undefined,
                    }}
                  >
                    <Page
                      pageNumber={paginaCorrente}
                      width={larghezza}
                      rotate={rotazione}
                      renderAnnotationLayer={false}
                      loading=""
                      onLoadSuccess={(pagina) => {
                        /*
                         Si registra la proporzione della pagina non ruotata: `rotate` la
                         cambierebbe, e riscriverla a ogni rotazione porterebbe a invertirla
                         due volte.
                         */
                        if (rotazione % 180 === 0 && pagina.width > 0) {
                          setProporzione(pagina.height / pagina.width);
                        }
                      }}
                    />
                  </CornicePagina>
                );
              })()}
          </Document>
        )}
      </AreaDocumento>
    </Contenitore>
  );
};

export default VisorePdf;
